import { forwardRef, SelectHTMLAttributes } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

interface Option {
  label: string;
  value: string | number;
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: Option[];
  placeholder?: string;
  hasError?: boolean;
}

const Wrapper = styled.div<{ $hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.borderStrong};
  }

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.borderFocus};
  }

  > svg {
    color: ${({ theme }) => theme.colors.textMuted};
    pointer-events: none;
    flex-shrink: 0;
  }
`;

const Field = styled.select`
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  appearance: none;
  -webkit-appearance: none;
  padding-right: 8px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.bgSurface};
  }
`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, hasError, className, ...rest }, ref) => (
    <Wrapper $hasError={hasError} className={className}>
      <Field ref={ref} aria-invalid={hasError || undefined} {...rest}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Field>
      <ChevronDown size={16} aria-hidden="true" />
    </Wrapper>
  ),
);

Select.displayName = 'Select';
